import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Markdown from '@frontastic/catwalk/src/js/component/markdown';
import MediaImage from '@frontastic/catwalk/src/js/mediaImage';

import styles from '../ProductPage.module.scss';

import { iconsAccordion } from '../options';
import { InformationIcon } from '@srcDS/components/atoms/icons';
import { getPrices } from '@srcDS/utils/helpers';
import { isFloat } from '@srcDS/utils/assertions';
import Price from '@srcDS/components/molecules/price';

export const useProductDetails = ({
  attributes,
  productDescription,
  data,
  currentSiteRegion,
  selectedVariant,
  selectedStore,
  storeList,
  dispatch,
}) => {
  // @ts-ignore
  const { currency } = useSelector((state) => state.app.context.currency);

  const {
    productDetailsIcon: ProductDetailsIcon,
    fabricAndCareIcon: FabricAndCareIcon,
    paymentOptionsIcon: PaymentOptionsIcon,
    deliveryAndReturnsIcon: DeliveryAndReturnsIcon,
  } = iconsAccordion;


  const currentSelectedStore = useMemo(() => {
    if (!storeList || !selectedStore?.channel?.id) return null;
    return storeList.find((item) => item.id === selectedStore.channel.id);
  }, [storeList, selectedStore]);


  const dataInfo = useMemo(() => {

    let infoSections = [
      {
        id: '1',
        icon: ProductDetailsIcon,
        title: 'PRODUCT DETAILS',
        markup: `<p>${productDescription}</p>`,
      },
      {
        id: '2',
        icon: FabricAndCareIcon,
        title: 'FABRIC & CARE',
        markup: `<div>
          <p class=${styles.deliveryDetailItem}>${attributes?.fabriccomposition || ''}</p>
          <p>${attributes?.careinstructions || ''}</p>
        </div>`,
      },
    ];

    // Add payment options section if available
    if (data?.paymentOptions) {
      const prices = selectedVariant?.projectSpecificData?.prices || [];
      const { price, discountedPrice } = getPrices(prices);
      const selectedPrice = discountedPrice ?? price;

      const options = data.paymentOptions.filter(
        (o) => o.showForCountry === 'all' || o.showForCountry === currentSiteRegion,
      );

      const getXPaymentPrice = (price, x) => {
        const langRegion = `en-${currentSiteRegion}`;
        const formatter = new Intl.NumberFormat(langRegion, {
          currency,
          minimumFractionDigits: 2,
        });
        const formatValue = Math.abs(price);
        const formattedPrice = (isFloat(formatValue) ? formatValue : formatValue / 100).toLocaleString(langRegion, {
          currency,
        });
        // @ts-ignore
        return parseFloat(formatter.format(formattedPrice / x)).toFixed(2);
      };

      infoSections.splice(1, 0, {
        id: '3',
        icon: PaymentOptionsIcon,
        title: 'PAYMENT OPTIONS',
        // @ts-ignore
        markup: (
          <ul className={styles.paymentOptions}>
            {options?.map(({ title, paymentInterval, logo }, index) => (
              <li key={index} className={styles.paymentOption}>
                <span className={styles.infoOption}>
                  {paymentInterval !== 0 && title && (
                    <>
                      {`x${paymentInterval} ${title}`}
                      <Price
                        // @ts-ignore
                        placement="widget"
                        type="regular"
                        value={Number(getXPaymentPrice(selectedPrice, paymentInterval))}
                        className={styles.priceOption}
                        plainPrice
                      />
                    </>
                  )}
                </span>
                <MediaImage
                  className={
                    logo?.title ? styles[`imageOption-${logo?.title['en_AU@AUD']}`] : styles.imageOptionDefault
                  }
                  media={logo}
                />
                <span
                  className={styles.paymentOptionIcon}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch({ type: 'open-paylater' });
                  }}
                >
                  {paymentInterval !== 0 && title && <InformationIcon />}
                </span>
              </li>
            ))}
          </ul>
        ),
      });
    }

    // Add delivery & returns section if available
    if (data?.mdText) {
      infoSections.push({
        id: '4',
        icon: DeliveryAndReturnsIcon,
        title: 'DELIVERY & RETURNS',
        // @ts-ignore
        markup: <Markdown text={data.mdText} className={styles.markupStyle} />,
      });
    }

    return infoSections;
  }, [attributes, productDescription, data, currentSiteRegion, selectedVariant, currency]);

  return {
    dataInfo,
    currentSelectedStore,
  };
};
